import { useState } from "react";
import { Button, Card, MUTED_TEXT, Select, Spinner, TextField, stackStyle } from "@dayvise/react-ui";
import { useToolPolicyMutation, useWorkspaceTools } from "./hooks.ts";

export function ToolPermissionsPanel() {
  const query = useWorkspaceTools();
  const mutation = useToolPolicyMutation();
  const [filter, setFilter] = useState("");
  const error = query.error ?? mutation.error;
  return (
    <Card title="Built-in tools" subtitle="Availability and permission are separate. Changing a permission does not install or configure a tool.">
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        {query.isPending ? <Spinner label="Loading tool permissions" /> : null}
        {error ? <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{error instanceof Error ? error.message : "Could not load or save tool permissions"}</p> : null}
        <div style={{ display: "flex", gap: "var(--dayvise-space-xs)", alignItems: "flex-end" }}>
          <TextField label="Find a tool" placeholder="browser, files, web…" value={filter} onChange={(event) => setFilter(event.target.value)} />
          <Button size="sm" variant="ghost" loading={query.isFetching} onClick={() => void query.refetch()}>Reload catalogue</Button>
        </div>
        <p style={{ ...MUTED_TEXT, margin: 0 }}>
          Reloading re-reads this workspace's tool catalogue from the server. It does not install, configure or approve anything.
        </p>
        {query.data?.tools.filter((tool) => `${tool.toolId} ${tool.description}`.toLowerCase().includes(filter.trim().toLowerCase())).map((tool) => (
          <Card key={tool.toolId} title={tool.toolId} subtitle={tool.description}>
            <p style={{ ...MUTED_TEXT, margin: "0 0 var(--dayvise-space-xs)" }}>
              {tool.executable === undefined ? "Availability has not been reported" : !tool.installed ? "Not implemented" : !tool.executable ? "Needs configuration" : tool.enabled ? tool.approvalRequired ? "Available with approval" : "Available" : "Unavailable under current policy or plan"} · {tool.riskLevel} risk
            </p>
            {tool.availabilityReason ? <p style={MUTED_TEXT}>{tool.availabilityReason}</p> : null}
            {tool.granted && !tool.executable ? <p style={MUTED_TEXT}>Permission is granted, but this tool cannot execute yet.</p> : null}
            {tool.toolId === "payments.charge" ? <p style={MUTED_TEXT}>This tool is separate from Dayvise subscription billing.</p> : null}
            {query.data.canManage ? (
              <Select
                label={`Permission for ${tool.toolId}`}
                value={tool.policyEffect ?? "default"}
                disabled={mutation.isPending}
                options={[{ value: "default", label: "Default policy" }, { value: "allow", label: "Allow" }, { value: "require_approval", label: "Require approval" }, { value: "deny", label: "Deny" }]}
                onChange={(event) => {
                  const effect = event.target.value;
                  mutation.mutate({ toolId: tool.toolId, effect: effect === "default" ? null : effect as "allow" | "require_approval" | "deny" });
                }}
              />
            ) : null}
          </Card>
        ))}
        {query.data && !query.data.canManage ? <p style={MUTED_TEXT}>An owner or admin can change workspace tool permissions.</p> : null}
        {query.data?.mcpServers?.length ? <Card title="Registered MCP servers" subtitle="Tool definitions and last known health are cached. Connections are checked when used.">
          {query.data.mcpServers.map((server) => <p key={server.mcpServerId}>
            {server.name} · {server.enabled ? "Enabled" : "Disabled"} · {server.discovered ? `${server.toolCount} discovered tools` : "Not discovered"} · {server.grantedToolCount} permitted tools · Last known health: {server.health}
          </p>)}
        </Card> : null}
      </div>
    </Card>
  );
}
